import { useEffect, useRef, useState } from 'react';
import './UsersPage.css';
import Loader from '../../components/Loader/Loader.jsx';
import { listUsers, getUserWithServers, toggleBan, updateBalance, updateBonusBalance } from '../../api/usersAPI/usersAPI.js';
import { listTransactions } from '../../api/transactionsAPI/transactionsAPI.js';
import { formatTxType } from '../../api/format/format.js';
import Pagination from '../../components/Pagination/Pagination.jsx';
import useLang from '../../hooks/use-lang/useLang.js';

const FILTER_OPTIONS = ['all', 'active', 'banned', 'partner'];

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 6;

function UsersPage({ onNavigate, onLogout }) {
  const { t } = useLang();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [page, setPage] = useState(1);
  const [pendingBan, setPendingBan] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [banMode, setBanMode] = useState('suspend');
  const [banInFlight, setBanInFlight] = useState(false);
  const [pendingBalance, setPendingBalance] = useState(null);
  const [balanceInput, setBalanceInput] = useState('');
  const [balanceInFlight, setBalanceInFlight] = useState(false);
  const [detailsUser, setDetailsUser] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedServer, setSelectedServer] = useState(null);
  const [serverTransactions, setServerTransactions] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const filterRef = useRef(null);
  const balanceInputRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setUsersLoading(true);
      listUsers({ search, status: filter })
        .then((data) => {
          setUsers(data);
          setUsersError(null);
        })
        .catch((err) => setUsersError(err.message))
        .finally(() => setUsersLoading(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search, filter]);

  function requestBan(user) {
    setPendingBan(user);
    setBanReason('');
    setBanMode('suspend');
  }

  function requestBalanceChange(user, field = 'balance') {
    setPendingBalance({ ...user, field });
    setBalanceInput('');
  }

  function cancelBalanceChange() {
    setPendingBalance(null);
    setBalanceInput('');
  }

  function requestDetails(user) {
    setDetailsUser({ ...user, servers: [] });
    setDetailsLoading(true);
    setSelectedServer(null);
    setServerTransactions(null);
    getUserWithServers(user.id)
      .then((full) => setDetailsUser(full))
      .catch(() => setDetailsUser(null))
      .finally(() => setDetailsLoading(false));
  }

  function closeDetails() {
    setDetailsUser(null);
    setSelectedServer(null);
    setServerTransactions(null);
  }

  function openServerDetails(server) {
    setSelectedServer(server);
    setServerTransactions(null);
  }

  function closeServerDetails() {
    setSelectedServer(null);
    setServerTransactions(null);
  }

  function viewTransactions() {
    if (!selectedServer) return;
    setTransactionsLoading(true);
    setServerTransactions([]);
    listTransactions({ serverId: selectedServer.id })
      .then(setServerTransactions)
      .catch(() => setServerTransactions([]))
      .finally(() => setTransactionsLoading(false));
  }

  function closeTransactions() {
    setServerTransactions(null);
  }

  function cancelBan() {
    setPendingBan(null);
    setBanReason('');
    setBanMode('suspend');
  }

  async function confirmBan(mode) {
    if (!pendingBan) return;
    const userId = pendingBan.id;
    const isBanning = pendingBan.status !== 'banned';
    if (isBanning && banReason.trim() === '') return;
    setBanInFlight(true);
    try {
      const result = await toggleBan(userId, isBanning ? banReason.trim() : null, isBanning ? mode : 'suspend');
      const nextStatus = result.is_banned ? 'banned' : 'active';
      setUsers((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, status: nextStatus } : user)),
      );
      setPendingBan(null);
      setBanReason('');
      setBanMode('suspend');
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setBanInFlight(false);
    }
  }

  async function confirmBalanceChange() {
    if (!pendingBalance) return;
    const parsed = parseFloat(balanceInput);
    if (Number.isNaN(parsed) || parsed < 0) return;
    const userId = pendingBalance.id;
    const field = pendingBalance.field === 'bonusBalance' ? 'bonusBalance' : 'balance';
    setBalanceInFlight(true);
    try {
      if (field === 'bonusBalance') {
        await updateBonusBalance(userId, parsed);
      } else {
        await updateBalance(userId, parsed);
      }
      setUsers((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, [field]: parsed } : user)),
      );
      setPendingBalance(null);
      setBalanceInput('');
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setBalanceInFlight(false);
    }
  }

  useEffect(() => {
    setPage(1);
  }, [search, filter]);

  const totalPages = Math.ceil(users.length / PAGE_SIZE);
  const filteredUsers = users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="users__page">
      <div className="page__container">
        <div className="users__page__header">
          <div className="filter__container" ref={filterRef}>
            <button
              className="users__filter-trigger"
              type="button"
              onClick={() => setIsFilterOpen((prev) => !prev)}
              aria-label={t('users.filterAria', 'Filter users')}
              aria-haspopup="listbox"
              aria-expanded={isFilterOpen}
            >
              <span>{t(`common.${filter}`, filter)}</span>
              <svg className="users__filter-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {isFilterOpen && (
              <ul className="users__filter-options" role="listbox">
                {FILTER_OPTIONS.map((value) => (
                  <li key={value} role="option" aria-selected={filter === value}>
                    <button
                      className={filter === value ? 'users__filter-option users__filter-option--active' : 'users__filter-option'}
                      type="button"
                      onClick={() => {
                        setFilter(value);
                        setIsFilterOpen(false);
                      }}
                    >
                      {t(`common.${value}`, value)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="search__input__container">
            <input
              className="users__search"
              type="text"
              placeholder={t('users.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t('users.searchAria', 'Search users')}
            />
          </div>
        </div>

        {usersLoading ? (
          <Loader text={t('users.loading')} />
        ) : (
        <div className="usersPage__card__container">
          {!usersLoading && usersError && <p className="users__empty">{t('common.error')}: {usersError}</p>}
          {!usersLoading && !usersError && filteredUsers.length === 0 && (
            <p className="users__empty">{t('users.empty')}</p>
          )}
          {!usersLoading && !usersError && filteredUsers.map((user) => (
            <div
              key={user.id}
              className={user.status === 'banned' ? 'users_card users_card--banned' : 'users_card'}
            >
              <div className="user__info__container">
                <div className="user__info__row">
                  <span className="user__info__label">{t('users.tgUsername')}</span>
                  <span className="user__info__value">{user.username}</span>
                </div>
                <div className="user__info__row">
                  <span className="user__info__label">{t('users.nickname')}</span>
                  <span className="user__info__value">{user.nickname}</span>
                </div>
                <div className="user__info__row">
                  <span className="user__info__label">{t('users.tgId')}</span>
                  <span className="user__info__value">{user.telegramId}</span>
                </div>
                <div className="user__info__row">
                  <span className="user__info__label">{t('users.balance')}</span>
                  <span className="user__info__value">{`$${user.balance.toFixed(2)}`}</span>
                </div>
                <div className="user__info__row">
                  <span className="user__info__label">{t('users.partner', 'Партнер')}</span>
                  <span className={user.isPartner ? 'user__status-badge user__status-badge--active' : 'user__status-badge'}>
                    {user.isPartner ? t('common.yes', 'Так') : t('common.no', 'Ні')}
                  </span>
                </div>
                {user.isPartner && (
                  <div className="user__info__row">
                    <span className="user__info__label">{t('users.bonusBalance', 'Бонусний баланс')}</span>
                    <span className="user__info__value">{`$${(user.bonusBalance ?? 0).toFixed(2)}`}</span>
                  </div>
                )}
                <div className="user__info__row">
                  <span className="user__info__label">{t('common.status')}</span>
                  <span className={user.status === 'banned' ? 'user__status-badge user__status-badge--banned' : 'user__status-badge user__status-badge--active'}>
                    {t(`common.${user.status}`, user.status)}
                  </span>
                </div>
              </div>

              <div className="users__controller__container">
                <div className="users__controller__row">
                  <button
                    className={user.status === 'banned' ? 'users__btn users__btn--unban' : 'users__btn users__btn--ban'}
                    type="button"
                    onClick={() => requestBan(user)}
                  >
                    {user.status === 'banned' ? t('users.unban') : t('users.ban')}
                  </button>
                  <button className="users__btn users__btn--details" type="button" onClick={() => requestDetails(user)}>{t('common.details')}</button>
                </div>
                <div className="users__controller__row">
                  <button className="users__btn users__btn--balance" type="button" onClick={() => requestBalanceChange(user)}>{t('users.changeBalance')}</button>
                  {user.isPartner && (
                    <button className="users__btn users__btn--balance" type="button" onClick={() => requestBalanceChange(user, 'bonusBalance')}>{t('users.changeBonusBalance', 'Бонусний баланс')}</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {!usersLoading && !usersError && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>

      {detailsUser && !selectedServer && (
        <div className="ban-modal-overlay" onClick={closeDetails}>
          <div className="ban-modal ban-modal--details" onClick={(e) => e.stopPropagation()}>
            <h3 className="ban-modal-title">{t('users.detailsTitle')}</h3>
            <div className="details-grid">
              <div className="details-row">
                <span className="details-label">{t('users.tgUsername')}</span>
                <span className="details-value">{detailsUser.username}</span>
              </div>
              <div className="details-row">
                <span className="details-label">{t('users.nickname')}</span>
                <span className="details-value">{detailsUser.nickname}</span>
              </div>
              <div className="details-row">
                <span className="details-label">{t('users.tgId')}</span>
                <span className="details-value">{detailsUser.telegramId}</span>
              </div>
              <div className="details-row">
                <span className="details-label">{t('users.balance')}</span>
                <span className="details-value">{`$${detailsUser.balance.toFixed(2)}`}</span>
              </div>
              <div className="details-row">
                <span className="details-label">{t('users.partner', 'Партнер')}</span>
                <span className={detailsUser.isPartner ? 'user__status-badge user__status-badge--active' : 'user__status-badge'}>
                  {detailsUser.isPartner ? t('common.yes', 'Так') : t('common.no', 'Ні')}
                </span>
              </div>
              {detailsUser.isPartner && (
                <div className="details-row">
                  <span className="details-label">{t('users.bonusBalance', 'Бонусний баланс')}</span>
                  <span className="details-value">{`$${(detailsUser.bonusBalance ?? 0).toFixed(2)}`}</span>
                </div>
              )}
              <div className="details-row">
                <span className="details-label">{t('common.status')}</span>
                <span className={detailsUser.status === 'banned' ? 'user__status-badge user__status-badge--banned' : 'user__status-badge user__status-badge--active'}>
                  {t(`common.${detailsUser.status}`, detailsUser.status)}
                </span>
              </div>
            </div>

            <div className="details-servers">
              <span className="details-servers-title">
                {t('users.servers')} {detailsLoading ? '…' : `(${detailsUser.servers.length})`}
              </span>
              {detailsLoading ? (
                <span className="details-servers-empty">{t('users.loadingServers')}</span>
              ) : detailsUser.servers.length === 0 ? (
                <span className="details-servers-empty">{t('users.noServers')}</span>
              ) : (
                <div className="details-servers-list">
                  {detailsUser.servers.map((server) => (
                    <div key={server.id} className="details-server-card" onClick={() => openServerDetails(server)}>
                      <div className="details-server-info">
                        <span className="details-server-name">{server.name}</span>
                        <span className="details-server-meta">{server.plan} · {server.location}</span>
                        <span className="details-server-ip">{server.ip}</span>
                      </div>
                      <div className="details-server-right">
                        <span className={`details-server-status details-server-status--${server.status}`}>
                          {t(`common.${server.status}`, server.status)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="ban-modal-actions">
              <button
                className="ban-modal-btn ban-modal-btn--close"
                type="button"
                onClick={closeDetails}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsUser && selectedServer && (
        <div className="ban-modal-overlay" onClick={closeDetails}>
          <div className="ban-modal ban-modal--details" onClick={(e) => e.stopPropagation()}>
            <button className="server-detail__back" type="button" onClick={closeServerDetails}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              {t('common.back')}
            </button>

            <div className="server-detail__header">
              <div className="server-detail__header-left">
                <span className="server-detail__name">{selectedServer.name}</span>
                <span className="server-detail__subtitle">{selectedServer.plan} · {selectedServer.provider} · {selectedServer.location}</span>
              </div>
              <span className={`server-detail__status server-detail__status--${selectedServer.status}`}>
                ● {t(`common.${selectedServer.status}`, selectedServer.status)}
              </span>
            </div>

            <div className="server-detail__section">
              <span className="server-detail__section-title">{t('users.serverSection')}</span>
              <div className="server-detail__grid">
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('users.exisId')}</span>
                  <span className="server-detail__value">#{selectedServer.id}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('users.providerId')}</span>
                  <span className="server-detail__value">{selectedServer.providerId}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('users.ipv4')}</span>
                  <span className="server-detail__value">{selectedServer.ip}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('common.os')}</span>
                  <span className="server-detail__value">{selectedServer.os}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('common.location')}</span>
                  <span className="server-detail__value">{selectedServer.location}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('common.created')}</span>
                  <span className="server-detail__value">{selectedServer.created}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('common.expires')}</span>
                  <span className="server-detail__value">{selectedServer.expires}</span>
                </div>
              </div>
            </div>

            <div className="server-detail__section">
              <span className="server-detail__section-title">{t('users.planSection')}</span>
              <div className="server-detail__grid">
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('common.plan')}</span>
                  <span className="server-detail__value">{selectedServer.plan}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">CPU</span>
                  <span className="server-detail__value">{selectedServer.cpu}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('users.ram')}</span>
                  <span className="server-detail__value">{selectedServer.ram}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('users.storage')}</span>
                  <span className="server-detail__value">{selectedServer.storage}</span>
                </div>
              </div>
            </div>

            <div className="server-detail__section">
              <span className="server-detail__section-title">{t('users.billingSection')}</span>
              <div className="server-detail__grid">
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('users.userPaid')}</span>
                  <span className="server-detail__value">{`$${selectedServer.paid.toFixed(2)}`}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('users.providerCost')}</span>
                  <span className="server-detail__value">{`$${selectedServer.cost.toFixed(2)}`}</span>
                </div>
                <div className="server-detail__row">
                  <span className="server-detail__label">{t('common.profit')}</span>
                  <span className="server-detail__value server-detail__profit">{`~$${(selectedServer.paid - selectedServer.cost).toFixed(2)}`}</span>
                </div>
              </div>
            </div>

            <button className="server-detail__transactions-btn" type="button" onClick={viewTransactions}>
              {t('users.viewTransactions')}
            </button>
          </div>
        </div>
      )}

      {detailsUser && selectedServer && serverTransactions && (
        <div className="ban-modal-overlay" onClick={closeDetails}>
          <div className="ban-modal ban-modal--details" onClick={(e) => e.stopPropagation()}>
            <div className="server-detail__header">
              <div className="server-detail__header-left">
                <span className="server-detail__name">{t('users.transactions')}</span>
                <span className="server-detail__subtitle">{detailsUser.username} · {selectedServer.name}</span>
              </div>
              <button className="server-detail__close" type="button" onClick={closeTransactions} aria-label={t('common.close')}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="server-detail__section">
              <span className="server-detail__section-title">
                {transactionsLoading ? t('users.loadingShort') : t('users.txCount').replace('{n}', serverTransactions.length)}
              </span>
              <div className="server-detail__grid">
                {serverTransactions.map((tx) => (
                  <div key={tx.id} className="server-detail__row">
                    <div className="server-detail__tx-left">
                      <span className="server-detail__tx-type">{t(`common.txType.${tx.type}`, formatTxType(tx.type))}</span>
                      <span className="server-detail__tx-date">{tx.date}</span>
                    </div>
                    <div className="server-detail__tx-right">
                      <span className={`server-detail__tx-amount server-detail__tx-amount--${tx.type}`}>
                        {tx.amount > 0 ? (tx.type === 'deposit' ? '+' : '-') : ''}${tx.amount.toFixed(2)}
                      </span>
                      <span className={`server-detail__tx-status server-detail__tx-status--${tx.status}`}>
                        {t(`common.${tx.status}`, tx.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {pendingBalance && (
        <div className="ban-modal-overlay" onClick={cancelBalanceChange}>
          <div className="ban-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ban-modal-title">
              {pendingBalance.field === 'bonusBalance'
                ? t('users.changeBonusBalanceTitle', 'Змінити бонусний баланс')
                : t('users.changeBalanceTitle')}
            </h3>
            <div className="ban-modal-user-info">
              <p className="ban-modal-text">
                {pendingBalance.nickname} ({pendingBalance.username})
              </p>
              <div className="ban-modal-current">
                <span className="ban-modal-current-label">{t('users.current')}</span>
                <span className="ban-modal-current-value">
                  {`$${(pendingBalance.field === 'bonusBalance' ? (pendingBalance.bonusBalance ?? 0) : pendingBalance.balance).toFixed(2)}`}
                </span>
              </div>
            </div>
            <label className="ban-modal-field">
              <span className="ban-modal-field-label">{t('users.newBalance')}</span>
              <input
                ref={balanceInputRef}
                className="ban-modal-input"
                type="text"
                inputMode="decimal"
                value={balanceInput}
                onChange={(e) => setBalanceInput(e.target.value)}
                placeholder={t('users.balancePlaceholder')}
                disabled={balanceInFlight}
                autoFocus
              />
            </label>
            <div className="ban-modal-actions">
              <button
                className="ban-modal-btn ban-modal-btn--cancel"
                type="button"
                onClick={cancelBalanceChange}
                disabled={balanceInFlight}
              >
                {t('common.cancel')}
              </button>
              <button
                className="ban-modal-btn ban-modal-btn--save"
                type="button"
                onClick={confirmBalanceChange}
                disabled={balanceInFlight || balanceInput === ''}
              >
                {balanceInFlight ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingBan && (
        <div className="ban-modal-overlay" onClick={cancelBan}>
          <div className="ban-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ban-modal-title">
              {pendingBan.status === 'banned' ? t('users.unbanTitle') : t('users.banTitle')}
            </h3>
            <p className="ban-modal-text">
              {(pendingBan.status === 'banned' ? t('users.unbanText') : t('users.banText'))
                .replace('{name}', pendingBan.nickname)
                .replace('{user}', pendingBan.username)}
            </p>
            {pendingBan.status !== 'banned' && (
              <div className="ban-modal-field">
                <span className="ban-modal-field-label">{t('common.reason')}</span>
                <input
                  className="ban-modal-input"
                  type="text"
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  disabled={banInFlight}
                  placeholder={t('users.banReasonPlaceholder')}
                  autoFocus
                />
              </div>
            )}
            <div className="ban-modal-actions ban-modal-actions--column">
              {pendingBan.status === 'banned' ? (
                <button
                  className="ban-modal-btn ban-modal-btn--unban"
                  type="button"
                  onClick={() => confirmBan('suspend')}
                  disabled={banInFlight}
                >
                  {banInFlight ? t('common.processing') : t('users.unban')}
                </button>
              ) : (
                <div className="ban-modal-actions-row">
                  <button
                    className="ban-modal-btn ban-modal-btn--ban"
                    type="button"
                    onClick={() => confirmBan('suspend')}
                    disabled={banInFlight || banReason.trim() === ''}
                  >
                    {banInFlight ? t('common.processing') : t('users.banSuspend')}
                  </button>
                  <button
                    className="ban-modal-btn ban-modal-btn--delete"
                    type="button"
                    onClick={() => confirmBan('delete')}
                    disabled={banInFlight || banReason.trim() === ''}
                  >
                    {banInFlight ? t('common.processing') : t('users.banDelete')}
                  </button>
                </div>
              )}
              <button
                className="ban-modal-btn ban-modal-btn--cancel"
                type="button"
                onClick={cancelBan}
                disabled={banInFlight}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;